import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import api from '../../utils/api';
import TechnicienLayout from '../../components/TechnicienLayout';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  name: string;
  phone: string;
  created_at: string;
  updated_at: string;
}

interface FormData {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface FormErrors {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  current_password?: string;
  new_password?: string;
  confirm_password?: string;
}

export default function AgentProfile() {
  const { user,type } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({ type: '', message: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (user) {
      setFormData(prevData => ({
        ...prevData,
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
      }));
    }
  }, [user]);

  const validateProfileForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = t('email_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('invalid_email');
    }

    if (!formData.first_name) {
      newErrors.first_name = t('first_name_required');
    }

    if (!formData.last_name) {
      newErrors.last_name = t('last_name_required');
    }

    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = t('invalid_phone');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.current_password) {
      newErrors.current_password = t('current_password_required');
    }
    if (!formData.new_password) {
      newErrors.new_password = t('new_password_required');
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = t('password_min_length');
    }
    if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = t('passwords_must_match');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateProfileForm()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(
        `/${type=='technicien' ? 'technicien/' : '' }profile`,
        {
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setToastMessage({ type: 'success', message: t('profile_updated') });
      setShowToast(true);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      setToastMessage({ type: 'error', message: t('update_failed') });
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(
        `/${type=='technicien' ? 'technicien/' : '' }change-password`,
        {
          current_password: formData.current_password,
          new_password: formData.new_password,
          new_password_confirmation: formData.confirm_password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setToastMessage({ type: 'success', message: t('password_updated') });
      setShowToast(true);
      
      setFormData(prevData => ({
        ...prevData,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
    } catch (error) {
      console.error('Error updating password:', error);
      setToastMessage({ type: 'error', message: t('password_update_failed') });
      setShowToast(true);
    } finally {
      setPasswordLoading(false);
    }
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  return (
    <>
    {type=='technicien' && <TechnicienLayout isHome={false}  refresh={()=>{}} />}
    <div className="bg-gray-100 min-h-screen p-4 md:p-6 lg:p-8">
      {showToast && (
        <div className={`absolute z-2000 top-4 right-4 p-4 rounded-md shadow-md ${
          toastMessage.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toastMessage.message}
        </div>
      )}
      <h1 className="text-3xl font-bold text-gray-800 mb-6">{t('agent_profile')}</h1>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-1">
            <div className="flex flex-col items-center mb-6">
              <div className="w-24 h-24 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-4xl mb-3">
                {formData.first_name[0]}{formData.last_name[0]}
              </div>
              <h2 className="text-2xl font-bold">{`${formData.first_name} ${formData.last_name}`}</h2>
              <p className="text-gray-500">{formData.email}</p>
            </div>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">{t('first_name')}</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${errors.first_name ? 'border-red-300' : 'border p-1'} focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
                />
                {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>}
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">{t('last_name')}</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${errors.last_name ? 'border-red-300' : 'border p-1'} focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
                />
                {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>}
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${errors.email ? 'border-red-300' : 'border p-1'} focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">{t('phone')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md shadow-sm ${errors.phone ? 'border-red-300' : 'border p-1'} focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
                />
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? t('updating...') : t('update_profile')}
              </button>
            </form>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold mb-4">{t('change_password')}</h3>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700">{t('current_password')}</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    id="current_password"
                    name="current_password"
                    value={formData.current_password}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm ${errors.current_password ? 'border-red-300' : 'border p-1'} focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOffIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                  </button>
                </div>
                {errors.current_password && <p className="mt-1 text-sm text-red-600">{errors.current_password}</p>}
              </div>
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">{t('new_password')}</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="new_password"
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm ${errors.new_password ? 'border-red-300' : 'border p-1'} focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOffIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                  </button>
                </div>
                {errors.new_password && <p className="mt-1 text-sm text-red-600">{errors.new_password}</p>}
              </div>
              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">{t('confirm_password')}</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirm_password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm ${errors.confirm_password ? 'border-red-300' : 'border p-1'} focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50`}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOffIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                  </button>
                </div>
                {errors.confirm_password && <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>}
              </div>
              <button
                type="submit"
                disabled={passwordLoading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${passwordLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {passwordLoading ? t('updating...') : t('update_password')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

